import Layout from '@/components/Layout';
import { ColorText } from '@/components/ColorSwatch';
import { motion } from 'framer-motion';

// ─── 演示数据 ─────────────────────────────────────────────────────

const SECTION_ONE = [
  { label: '主色', value: '#3B82F6' },
  { label: '背景色', value: '#FFFFFF' },
  { label: '正文色', value: '#111827' },
  { label: '短 HEX', value: '#abc' },
  { label: '混合大小写', value: '#fFaa00' },
  { label: 'RGB 蓝色', value: 'rgb(59, 130, 246)' },
  { label: 'RGB 深色', value: 'rgb(15, 23, 42)' },
  { label: 'RGB 黑色', value: 'rgb(0, 0, 0)' },
  { label: 'RGB 白色', value: 'rgb(255, 255, 255)' },
  { label: 'HSL 蓝色', value: 'hsl(217, 91%, 60%)' },
  { label: 'HSL 橙色', value: 'hsl(38, 92%, 50%)' },
  { label: 'HSL 黑色', value: 'hsl(0, 0%, 0%)' },
  { label: 'HSL 白色', value: 'hsl(0, 0%, 100%)' },
  { label: 'HSL 红色', value: 'hsl(360, 100%, 50%)' },
];

const SECTION_TWO = [
  '主色#3B82F6背景#FFFFFF按钮#123123啊啊边框#abc结束。',
  '(#123456)，[#654321]，色值是#00ff88。',
  '导航rgb(59, 130, 246)卡片rgb(15, 23, 42)结束。',
  '渐变hsl(217, 91%, 60%)警告hsl(38, 92%, 50%)结束。',
];

const SECTION_THREE = [
  { label: '过长 HEX', value: '#123123123123' },
  { label: '嵌入英文', value: 'abc#123123' },
  { label: 'HEX 后紧贴英文', value: '#123123text' },
  { label: 'URL hash', value: 'https://example.com/page#123123' },
  { label: '非法 HEX', value: '#GGGGGG' },
  { label: '位数不对', value: '#12   #1234   #12345' },
  { label: 'RGB 超范围', value: 'rgb(999, 1, 1)' },
  { label: 'RGB 参数不完整', value: 'rgb(1, 2)' },
  { label: 'RGB 负数', value: 'rgb(1, -2, 3)' },
  { label: 'HSL 饱和度超范围', value: 'hsl(217, 101%, 60%)' },
  { label: 'HSL 亮度超范围', value: 'hsl(217, 91%, 120%)' },
  { label: 'HSL 缺百分号', value: 'hsl(217, 91, 60%)' },
  { label: '嵌入英文', value: 'theme-rgb(59, 130, 246)' },
  { label: '嵌入英文', value: 'colorhsl(217, 91%, 60%)' },
];

// ─── 子组件 ───────────────────────────────────────────────────────

function SectionHeader({ num, title, desc }: { num: string; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-4 mb-6">
      <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg flex-shrink-0">
        {num}
      </div>
      <div>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
        <p className="text-muted-foreground text-sm mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

function Row({ label, value, showSwatch = true }: { label: string; value: string; showSwatch?: boolean }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
      <span className="w-32 text-sm text-muted-foreground flex-shrink-0">{label}</span>
      <code className="flex-1 text-sm font-mono bg-muted/60 rounded px-2 py-0.5 text-foreground">
        {showSwatch ? <ColorText>{value}</ColorText> : value}
      </code>
    </div>
  );
}

// ─── 页面 ─────────────────────────────────────────────────────────

export default function ColorSwatchDemo() {
  return (
    <Layout>
      {/* 页面标题 */}
      <section className="bg-gradient-to-b from-primary/8 to-background pt-16 pb-10 border-b border-border">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Design System</p>
            <h1
              className="text-4xl font-bold text-foreground mb-3"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              颜色值色卡展示
            </h1>
            <p className="text-muted-foreground text-base max-w-xl">
              在文本中内联识别 HEX / RGB / HSL 颜色值，合法色值将自动附带可视色卡；
              悬停色卡可查看颜色原始值。
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6 max-w-4xl py-12 space-y-12">

        {/* ── 一、独立色值（应显示色卡） ── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <SectionHeader
            num="一"
            title="独立色值 — 应该显示色卡"
            desc="HEX / RGB / HSL 格式的合法颜色值，前后均为空格或行首行尾"
          />
          <div className="bg-card border border-border rounded-2xl px-6 py-2 shadow-sm">
            {SECTION_ONE.map((item) => (
              <Row key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
        </motion.section>

        {/* ── 二、紧贴中文或标点（应显示色卡） ── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <SectionHeader
            num="二"
            title="紧贴中文或标点 — 应该显示色卡"
            desc="颜色值紧贴中文字符、括号、逗号等标点符号时，仍可正确识别"
          />
          <div className="bg-card border border-border rounded-2xl px-6 py-2 shadow-sm">
            {SECTION_TWO.map((line, i) => (
              <div key={i} className="py-3 border-b border-border last:border-0">
                <p className="text-sm font-mono text-foreground leading-relaxed">
                  <ColorText>{line}</ColorText>
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* ── 三、不应显示色卡 ── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <SectionHeader
            num="三"
            title="非法或特殊情形 — 不应该显示色卡"
            desc="位数错误、范围越界、嵌入英文、URL hash 等情形，不应渲染色卡"
          />
          <div className="bg-card border border-border rounded-2xl px-6 py-2 shadow-sm">
            {SECTION_THREE.map((item) => (
              <Row key={item.label + item.value} label={item.label} value={item.value} />
            ))}
          </div>
        </motion.section>

      </div>
    </Layout>
  );
}
